import { useMemo, useSyncExternalStore } from 'react';
import {
  chartSeries,
  getThemeVersion,
  subscribeTheme,
  themePalette,
  type ThemePalette,
} from './tokens';

/**
 * Re-render the caller when the active theme changes. Returns the theme version,
 * suitable as a `useMemo` dependency so imperatively-built artifacts (ECharts
 * options, canvas) rebuild with the new tokens on a theme flip.
 */
export function useThemeVersion(): number {
  return useSyncExternalStore(subscribeTheme, getThemeVersion, () => 0);
}

/**
 * The semantic role palette, refreshed on theme change. Memoized by theme
 * version so the reference is stable between flips — consumer `useMemo`s that
 * depend on it don't recompute every render, only when the theme actually changes.
 */
export function useThemeColors(): ThemePalette {
  const version = useThemeVersion();
  // `version` is the reactivity trigger: themePalette() reads the DOM, not the
  // variable, so the linter can't see the dependency is load-bearing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => themePalette(), [version]);
}

/** The 10-hue categorical chart series, refreshed on theme change. */
export function useChartSeries(): string[] {
  const version = useThemeVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => chartSeries(), [version]);
}
